import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Search } from "lucide-react"
import {
  DataTable,
  EmptyState,
  ErrorState,
  MoneyText,
  SegmentedControl,
  StatusPill,
  en,
  formatDateTime,
} from "procurement/design"
import {
  useGetBudgetDashboardDetailOrdersQuery,
  useGetBudgetDashboardDetailQuery,
} from "shared/redux/endpoints/procurementV2"
import OrderDetailTable from "./OrderDetailTable"
import useProcurementFilters from "../../hooks/useProcurementFilters"
import useDebouncedValue from "../../hooks/useDebouncedValue"

const TYPE = {
  OR_LINE: { label: "OR line", tone: "info", order: 3 },
  PO_HEADER: { label: "PO", tone: "success", order: 0 },
  PO_LINE: { label: "PO line", tone: "neutral", order: 1 },
  PAYMENT_LINK: { label: "Payment", tone: "warning", order: 2 },
}

const codeOf = (r) =>
  ({
    OR_LINE: r.orCode,
    PO_HEADER: r.poCode,
    PO_LINE: r.orCode,
    PAYMENT_LINK: r.prCode,
  }[r.recordType] ?? "—")

const statusOf = (r) =>
  ({
    OR_LINE: r.orLineStatus || r.orStatus,
    PO_HEADER: r.poStatus,
    PAYMENT_LINK: r.prStatus,
  }[r.recordType] ?? null)

const amountOf = (r) =>
  ({
    OR_LINE: r.orRequestedAmount,
    PO_HEADER: r.poTotalAmount,
    PO_LINE: r.poLineOrAmountReference,
    PAYMENT_LINK: r.prLinkAmount,
  }[r.recordType] ?? null)

const linkOf = (r) => {
  if (r.recordType === "PAYMENT_LINK" && r.paymentRequestId)
    return `/procurement/payment-requests/${r.paymentRequestId}`
  if (r.recordType === "OR_LINE") return r.orderRequestId ? `/procurement/orders/${r.orderRequestId}` : null
  return r.purchaseId ? `/procurement/purchases/${r.purchaseId}` : null
}

const COLUMNS = [
  {
    key: "recordType",
    header: "Type",
    sortable: true,
    value: (r) => TYPE[r.recordType]?.order,
    render: (r) => (
      <StatusPill tone={TYPE[r.recordType]?.tone || "neutral"} label={TYPE[r.recordType]?.label || r.recordType} />
    ),
  },
  {
    key: "code",
    header: "Code",
    sortable: true,
    value: codeOf,
    render: codeOf,
    className: "pc-col-nowrap",
  },
  {
    key: "documentEventAtLocal",
    header: "Date",
    sortable: true,
    className: "pc-col-nowrap",
    render: (r) => (r.documentEventAtLocal ? formatDateTime(r.documentEventAtLocal) : "—"),
  },
  { key: "budgetCenterName", header: "Center", sortable: true },
  { key: "categoryName", header: "Category", sortable: true },
  {
    key: "itemName",
    header: "Item",
    sortable: true,
    className: "pc-col-title",
  },
  {
    key: "status",
    header: "Status",
    sortable: true,
    value: statusOf,
    render: (r) => statusOf(r) || "—",
  },
  {
    key: "amount",
    header: "Amount (ref.)",
    sortable: true,
    align: "right",
    tooltip: "PO / payment amounts are whole-PO references — never sum them across rows",
    value: amountOf,
    render: (r) => <MoneyText value={amountOf(r)} />,
  },
]

// Group each PO's header, lines and payment links under one parent so sort
// and paging keep them together; OR lines without a PO stay standalone.
const groupByPurchase = (items) => {
  const groups = new Map()
  const loose = []
  items.forEach((r) => {
    const row = { ...r, key: r.recordKey }
    if (!r.purchaseId) {
      loose.push(row)
      return
    }
    if (!groups.has(r.purchaseId)) groups.set(r.purchaseId, [])
    groups.get(r.purchaseId).push(row)
  })
  const grouped = [...groups.values()].flatMap((rows) => {
    const [head, ...rest] = rows.sort((a, b) => TYPE[a.recordType]?.order - TYPE[b.recordType]?.order)
    return [{ ...head, isParent: true }, ...rest.map((r) => ({ ...r, depth: 1 }))]
  })
  return [...grouped, ...loose]
}

const ORDER_PAGE_SIZE = 20

const DOCUMENT_TABS = [
  { value: "", label: "All" },
  { value: "OR", label: "Order" },
  { value: "PO", label: "PO" },
  { value: "PR", label: "Payment" },
]

const BudgetDetailTable = ({ params }) => {
  const navigate = useNavigate()
  const { filters, setFilters } = useProcurementFilters({
    docType: "",
    docKeyword: "",
  })
  const keyword = useDebouncedValue(filters.docKeyword.trim(), 400)
  const ready = Boolean(params.centerId || params.categoryId)
  const isOrders = filters.docType === "OR"
  const [orderPage, setOrderPage] = useState(0)
  useEffect(() => setOrderPage(0), [params, keyword])

  const records = useGetBudgetDashboardDetailQuery(
    { ...params, documentType: filters.docType, keyword, size: 1000 },
    { skip: !ready || isOrders }
  )
  const orders = useGetBudgetDashboardDetailOrdersQuery(
    { ...params, keyword, page: orderPage, size: ORDER_PAGE_SIZE },
    { skip: !ready || !isOrders }
  )
  const { data, isFetching, isError } = isOrders ? orders : records
  const rows = useMemo(() => groupByPurchase(records.data?.items || []), [records.data])

  if (!ready)
    return <EmptyState title="Pick a center or a category" text="The drill-down needs at least one of them." />

  return (
    <>
      <div className="ds-table-toolbar">
        <SegmentedControl
          value={filters.docType}
          onChange={(value) => setFilters({ docType: value })}
          options={DOCUMENT_TABS}
        />
        <div className="ds-table-toolbar-right">
          <span className="pc-hint">{isFetching ? "Loading…" : en.common.results(data?.total ?? 0)}</span>
          <div className="pc-input-wrap br-search">
            <Search className="pc-lead" aria-hidden />
            <input
              type="search"
              className="pc-control pc-has-lead"
              placeholder="Search OR / PO / PR code, item…"
              aria-label="Search detail records"
              value={filters.docKeyword}
              onChange={(e) => setFilters({ docKeyword: e.target.value })}
            />
          </div>
        </div>
      </div>
      {isError ? (
        <ErrorState text="Couldn't load the detail records." />
      ) : isOrders ? (
        <OrderDetailTable
          data={data}
          isFetching={isFetching}
          page={orderPage}
          size={ORDER_PAGE_SIZE}
          onPageChange={setOrderPage}
          navigate={navigate}
        />
      ) : (
        <DetailRows rows={rows} isFetching={isFetching} navigate={navigate} />
      )}
    </>
  )
}

const DetailRows = ({ rows, isFetching, navigate }) => (
  <DataTable
    columns={COLUMNS}
    rows={rows}
    isLoading={isFetching}
    density="compact"
    onRowClick={(r) => {
      const to = linkOf(r)
      if (to) navigate(to)
    }}
    empty={<EmptyState title="No records for these filters." />}
  />
)

export default BudgetDetailTable
